import Breadcrumb from "@/components/base/Breadcrumb";
import Gallery from "./components/Gallery";
import usePageMeta from "@/hooks/usePageMeta";
import {
  profileAchievements,
  profileFacts,
  profileGallery,
  profileIntro,
  profileLinks,
  profileTimeline,
} from "@/mocks/profile";

export default function Profile() {
  usePageMeta({
    title: "森泽佳奈个人资料",
    description:
      "森泽佳奈个人资料页，包含她的出生信息、演艺经历、艺术成就与专业背景，全面了解这位多领域的创作者。",
    keywords: "森泽佳奈个人资料,森泽佳奈介绍,森泽佳奈经历,森泽佳奈成就",
  });

  return (
    <div className="bg-background-50">
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: "个人资料" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4">
            森泽佳奈个人资料
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">
            全面了解森泽佳奈的成长轨迹、艺术成就与专业背景。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
              <img
                src={profileIntro.photo}
                alt={profileIntro.name}
                title="森泽佳奈个人形象照"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-accent-600 mb-3">
              <span className="w-6 h-px bg-accent-500"></span>
              Profile
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {profileIntro.name}
            </h2>
            <p className="text-sm text-primary-600 font-medium mt-1">{profileIntro.title}</p>
            <p className="text-base text-foreground-700 italic mt-4 border-l-2 border-accent-400 pl-4">
              {profileIntro.tagline}
            </p>
            {profileIntro.bio.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <i className="ri-information-line text-primary-500"></i>
              基本信息
            </h2>
            <ul className="flex flex-col divide-y divide-background-200 bg-background-50 rounded-lg border border-background-200 px-5">
              {profileFacts.map((f) => (
                <li key={f.label} className="flex items-center justify-between gap-4 py-3.5">
                  <span className="text-sm text-foreground-500 whitespace-nowrap">{f.label}</span>
                  <span className="text-sm text-foreground-900 text-right">{f.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <i className="ri-trophy-line text-accent-500"></i>
              艺术成就
            </h2>
            <ul className="flex flex-col gap-3">
              {profileAchievements.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200"
                >
                  <i className="ri-star-smile-line text-accent-500 mt-0.5"></i>
                  <span className="text-sm text-foreground-700 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 flex items-center gap-2">
          <i className="ri-route-line text-primary-500"></i>
          主要经历
        </h2>
        <ol className="relative border-l border-background-300 ml-2 md:ml-3">
          {profileTimeline.map((t) => (
            <li key={t.year} className="relative pl-8 pb-8 last:pb-0">
              <span className="absolute -left-[7px] top-1 w-3.5 h-3.5 rounded-full bg-accent-500 ring-4 ring-background-50"></span>
              <span className="font-heading text-base font-semibold text-primary-600">{t.year}</span>
              <h3 className="font-medium text-foreground-950 mt-1">{t.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-1.5 max-w-2xl">
                {t.description}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
          精彩瞬间
        </h2>
        <p className="text-sm text-foreground-600 mb-6">点击图片可放大预览，使用左右方向键切换。</p>
        <Gallery images={profileGallery} />
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          相关链接
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {profileLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                <i className="ri-external-link-line text-lg"></i>
              </span>
              <span className="flex flex-col">
                <span className="font-medium text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {link.name}
                </span>
                <span className="text-xs text-foreground-500 mt-1 leading-relaxed">{link.description}</span>
              </span>
            </a>
          ))}
        </div>
        <p className="text-xs text-foreground-400 mt-4">
          注：以上链接均为演示示例，不代表真实第三方页面。
        </p>
      </section>
    </div>
  );
}